import { ApiRequestOptions, ApiService } from '../../../common/services/api/api.service';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map, of, switchMap } from 'rxjs';
import { VisitorEvent } from '../../shared/models/visitor.event';
import { EventTimeService } from './event.time.service';
import { CalendarQueryCommand } from '../../shared/models/calendar.action.command';
import { isEmptyArray } from 'src/app/common/utils/object.extensions';
import { CreateEventCommand } from '../../shared/models/create.event.command';
import { ActivatedRoute } from '@angular/router';
import { ErrorAlertService } from 'src/app/common/services/error.alert/error.alert.service';
import { IAppState, IFrontendSettings } from '../../shared/ngrx.stores/frontend.settings/states';
import { Store } from '@ngrx/store';
import * as EventQueryCommandActions from 'src/app/modules/event-calendar/ngrx.stores/event.query.command/actions';
import { EditEventCommand } from '../../shared/models/edit.event.command';

@Injectable({
  providedIn: 'root',
})
export class VisitorEventService {
  static itemCreatedSubject$ = new BehaviorSubject<any>(null);

  constructor(
    private _apiService: ApiService,
    private _eventTimeService: EventTimeService,
    private _errorAlertService: ErrorAlertService,
    private _store: Store<IAppState>
  ) { }

  public getEvents(hubName: string, paramObj: CalendarQueryCommand): Observable<VisitorEvent[]> {
    return this._apiService.get(`visitors/${hubName}/events`, paramObj).pipe(
      map((events) => {
        events.forEach((event) => {
          this._eventTimeService.processEventTimes(event);
        });
        return events;
      })
    );
  }

  public getLocationFilter(hubName: string): Observable<{ locality: string }[]> {
    return this._apiService.get(`visitors/${hubName}/events/location-filter`);
  }

  public buildFilterEventsByDateCommandFromUrlQuery(
    activatedRoute: ActivatedRoute
  ): Observable<CalendarQueryCommand> {
    return activatedRoute.queryParams.pipe(
      switchMap((params) => {
        console.log('params', params);

        let { date, start, end } = params;

        if (start && end) {
          date = 'specific';
        }

        if (!date && !start && !end) {
          if (['start', 'end', 'date'].any((x) => window.location.search.includes(x))) {
            const searchParams = new URLSearchParams(window.location.search.split('?')[1]);
            for (const [key, value] of searchParams) {
              if (key === 'date') date = value;
              if (key === 'start') start = value;
              if (key === 'end') end = value;
            }
            console.log(`date: ${date}, start: ${start}, end: ${end}`);
          }
        }

        const command = new CalendarQueryCommand();

        if (!date && !start && !end) {
          return of(null);
        }

        if (date) {
          switch (date) {
            case 'all':
              break;
            case 'today':
              ({ from: command.from, to: command.to } = this._eventTimeService.getTodayRange());
              command.dateRange = 'today';
              break;
            case 'tomorrow':
              ({ from: command.from, to: command.to } = this._eventTimeService.getTomorrowRange());
              command.dateRange = 'tomorrow';
              break;
            case 'this-week':
              ({ from: command.from, to: command.to } = this._eventTimeService.getWeekRange());
              command.dateRange = 'this-week';
              break;

            case 'this-weekend': {
              const now = new Date(); // local
              const day = now.getDay(); // 0 = Sun … 6 = Sat

              // Move to *this* Friday
              const daysToFriday = day <= 5 ? 5 - day : -(day - 5);

              /* Friday 00:00 LOCAL */
              const fridayLocal = new Date(
                now.getFullYear(),
                now.getMonth(),
                now.getDate() + daysToFriday,
                0,
                0,
                0,
                0
              );

              /* Sunday 23:59:59.999 LOCAL */
              const sundayLocal = new Date(fridayLocal);
              sundayLocal.setDate(fridayLocal.getDate() + 2);
              sundayLocal.setHours(23, 59, 59, 999);

              ({ from: command.from, to: command.to } =
                this._eventTimeService.getWeekendRangeInTimezone());
              command.dateRange = 'this-weekend';
              break;
            }

            case 'this-month':
              ({ from: command.from, to: command.to } = this._eventTimeService.getMonthRange());
              command.dateRange = 'this-month';
              break;
            case 'specific':
              if (!start && !end) {
                // If ?date=specific but no start/end params, try to get from URL params or set to all
                // Don't redirect automatically - let the component handle it
                // This allows the component to maintain state if dates are set via store
                return of(null);
              } else {
                ({ from: command.from, to: command.to } =
                  this._eventTimeService.getSpecificRangeFromDateStrings(start, end));
                command.dateRange = 'specific';
              }
              break
            default:
              this._errorAlertService.showErrorAlert(
                'Invalid date, only "today", "tomorrow", "this-week", "this-month" and "this-weekend" are supported.'
              );
          }
        } else if (start && end) {
          ({ from: command.from, to: command.to } =
            this._eventTimeService.getSpecificRangeFromDateStrings(start, end));
          command.dateRange = 'specific'; // Set dateRange so highlighting works
        } else {
          this._errorAlertService.showErrorAlert(
            'Invalid date, please specify start date and end date.'
          );
        }

        return of(command);
      })
    );
  }

  public buildFilterEventsByCustomCategoryCommandFromUrlQuery(
    activatedRoute: ActivatedRoute
  ): Observable<CalendarQueryCommand> {
    return activatedRoute.queryParams.pipe(
      switchMap((params) => {
        const searchParams = new URLSearchParams(
          window.location.search.split('?')[1] || ''
        );

        // Locked base category (embedded widget). Format: name-id (id is last segment after '-').
        let lockedCategoryId = '';
        const baseRaw = searchParams.get('ig-base-category');
        if (baseRaw) {
          lockedCategoryId = baseRaw.includes('-') ? baseRaw.split('-').last() : baseRaw;
        }

        // User-driven custom-events selection
        const rawValue = window.location.search.includes('ig-custom-events')
          ? searchParams.get('ig-custom-events') || ''
          : '';

        if (!rawValue && !lockedCategoryId) {
          return of(null);
        }

        const command = new CalendarQueryCommand();
        if (lockedCategoryId) {
          command.lockedCategoryId = lockedCategoryId;
        }

        if (rawValue) {
          // Check for multi-select (comma-separated values)
          if (rawValue.includes(',')) {
            const ids = rawValue.split(',').map((segment) => {
              return segment.includes('-') ? segment.split('-').last() : segment;
            });
            command.favoriteEventBtnIds = ids.join(',');
            command.matchAllCategories = searchParams.get('ig-match-all') === 'true';
          } else {
            const favoriteEventId = rawValue.includes('-')
              ? rawValue.split('-').last()
              : rawValue;
            if (lockedCategoryId) {
              // Locked mode: keep AND semantics even for a single value
              command.favoriteEventBtnIds = favoriteEventId;
              command.matchAllCategories = true;
            } else {
              // Single-select (backward compat)
              command.favoriteEventBtnId = favoriteEventId;
            }
          }
        } else if (lockedCategoryId) {
          // Fresh embed load: only the locked base, no extra selection yet
          command.favoriteEventBtnIds = lockedCategoryId;
          command.matchAllCategories = true;
        }

        return of(command);
      })
    );
  }

  public createEvent(hubName, command: CreateEventCommand) {
    const options = new ApiRequestOptions();
    options.suppressErrorHandler = true;
    options.timeout = 60 * 1000;
    return command.isHubCreated
      ? this._apiService.post(`hubs/${hubName}/events`, command, options)
      : this._apiService.post(`visitors/${hubName}/events`, command, options);
  }

  public isNormalTicketUri(event: VisitorEvent): boolean {
    if (!event) return false;
    return (
      event.ticketUri &&
      (isEmptyArray(event.ticketInfo) || event.ticketUri !== event.ticketInfo[0].link)
    );
  }

  public venueName(event: VisitorEvent): string {
    if (!event) return '';
    return event.venue || event.venueInfo?.name || event.owner?.name;
  }

  public processedTicketUri(ticketUri: string): string {
    let noSchema = ticketUri.replace('https://', '');
    noSchema = noSchema.replace('http://', '');
    noSchema = noSchema.replace('www.', '');
    return noSchema.split('/')[0];
  }

  public openTicketUrl(link: string) {
    window.open(link.startsWith('http') ? link : `https://${link}`, '_blank');
  }

  public visitWebsite(event: VisitorEvent) {
    if (event.eventLink || event.hostLink) {
      window.open(event.eventLink || event.hostLink, '_blank');
    }
  }

  getDestinationEventFeeds(hubName: string): Observable<any> {
    return this._apiService.get(`visitors/${hubName}/events/events-for-travel-buddy`).pipe(
      map((events) => {
        // move the processing to ai.buddy to speed up
        // events.forEach((event) => {
        //   this._eventTimeService.processEventTimes(event);
        // });
        return events;
      })
    );
  }
}
